// apps/merchant-v3/src/components/TierBadge.tsx
//
// dbg-20260929-loyaltytier — ONE tier badge, drawn the same way everywhere.
//
// The badge existed in four places with two different colour rules:
//
//   ProfileDropdown.tsx           per-tier colours (Diamond cyan, Platinum
//                                 grey, Titanium amber)
//   catalog/ProductCard.tsx       `bg-brand-50 text-brand-700` — fixed
//   wizard/BlueprintCard.tsx      `bg-brand-50 text-brand-700` — fixed
//   catalog/BlueprintDetailPage   `bg-brand-50 text-brand-700` — fixed
//
// `--color-brand-50` is #fff3ee, a warm orange. So a Diamond merchant saw a
// cyan badge in the top bar and an orange one on every product card, for the
// same tier, on the same screen — reported exactly that way.
//
// Three copies of one literal is how that happens: nothing links them, so a
// colour decision made in one place cannot reach the others. This component is
// the single place the badge is described; the four call sites render it.
//
// The per-tier palette wins over the flat brand colour because it carries
// information — a merchant learns their rung by its colour — and because the
// dropdown is where the tier is most often read.
import { useTranslation } from 'react-i18next';

import type { LoyaltyTier } from '@/types/user';
import { cn } from '@/lib/cn';

/**
 * Badge colours per rung.
 *
 * The ladder has SIX rungs (Titanium, Platinum, Diamond, E1, E2, E3 — see
 * catalogPermissions.seed.ts `tierLadder`) and is editable data, so an unknown
 * label gets the neutral style rather than borrowing another rung's colour.
 * `Titanium` is an explicit case, never the `default`: making it the default is
 * what let an unrecognised tier read as Titanium.
 */
function tierClasses(tier: string): string {
  switch (tier) {
    case 'Diamond':
      return 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200';
    case 'Platinum':
      return 'bg-pw-surface text-pw-text-muted ring-1 ring-pw-border';
    case 'Titanium':
      return 'bg-amber-50 text-amber-800 ring-1 ring-amber-200';
    default:
      return 'bg-pw-surface text-pw-text-muted ring-1 ring-pw-border';
  }
}

export interface TierBadgeProps {
  /** The tier label from the server. `null`/`undefined` renders NOTHING —
   *  never a default (dbg-20260929-loyaltytier). */
  tier: LoyaltyTier | null | undefined;
  /** Extra positioning classes from the call site (e.g. `ml-auto`). */
  className?: string;
  /** When true, adds the "this is the price for your tier" tooltip. The
   *  profile dropdown omits it — there the badge is an identity, not an
   *  explanation of a nearby number. */
  withPriceTooltip?: boolean;
  testId?: string;
}

export function TierBadge({
  tier,
  className,
  withPriceTooltip = false,
  testId = 'tier-badge',
}: TierBadgeProps): JSX.Element | null {
  const { t } = useTranslation('products');
  if (!tier) return null;
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        tierClasses(tier),
        className,
      )}
      title={
        withPriceTooltip
          ? t('card.tierTooltip', {
              defaultValue: 'Tier-resolved price for {{tier}} merchants',
              tier,
            })
          : undefined
      }
      data-testid={testId}
    >
      {tier}
    </span>
  );
}

export default TierBadge;
